/**
 * The SENTENCES the grid speaks to a screen reader. Pure string builders — no
 * React, no DOM, no state — so every wording rule is unit-testable on its own.
 *
 *   what the user did          what is announced
 *   ─────────────────────────  ──────────────────────────────────────────
 *   applied 3 filters          "3 filters applied, 47 results"
 *   cleared them               "Filters cleared, 312 results"
 *   searched "alpha"           "Search "alpha", 12 results"
 *   picked a quick filter      "Showing Overdue, 8 results"
 *   filtered down to nothing   "1 filter applied, no results.
 *                               Try adjusting filters or clear them all."
 *
 * ── ONE VOICE, ON PURPOSE ──────────────────────────────────────────────
 *
 * The empty case is spoken HERE, not by a second live region on
 * `NoRowsOverlay`. Two polite regions firing for ONE user action is the
 * failure mode this whole layer exists to avoid — the user would hear
 * "…, no results" and then "No matching results. Try adjusting filters or
 * clear them all." back to back for a single click. So the overlay's own
 * guidance sentence is exported from here ({@link NO_RESULTS_GUIDANCE}),
 * rendered by the overlay for the eye and folded into the announcement for
 * the ear. They cannot drift, and neither can double-speak.
 *
 * The overlay is also the WRONG owner mechanically: `mode="local"` never
 * mounts it (MUI's default overlay renders instead), so a region there would
 * cover one mode and silently miss the other.
 *
 * WHAT IS DESCRIBED: only the axis that CHANGED. Restating the criteria a
 * user did not touch is how a live region turns into noise — "Showing All,
 * 0 filters applied, search "", 312 results" on every click.
 */
import type { ServerGridFilter } from '../shared/gridFilterTypes';

/**
 * ONE utterance: the sentence, and a counter that moves every time the grid
 * has something to say.
 *
 *   name = Alpha  ──►  "1 filter applied, 1 result"   seq 1
 *   name = Bravo  ──►  "1 filter applied, 1 result"   seq 2
 *                       └ same words, second utterance
 *
 * Two consecutive announcements can carry the SAME sentence — editing a
 * filter's value leaves both the filter count and the result count where they
 * were. Assistive tech speaks a DOM mutation, so the sentence alone cannot
 * drive the region: setting an identical string bails out of the re-render and
 * the text node never moves. `seq` is what {@link GridLiveRegion} keys on.
 */
export type GridAnnouncement = { text: string; seq: number };

/** The region mounted and with nothing to say. */
export const SILENT_ANNOUNCEMENT: GridAnnouncement = { text: '', seq: 0 };

/** The next utterance after `previous`, whether or not the words changed. */
export const nextAnnouncement = (previous: GridAnnouncement, text: string): GridAnnouncement => ({
  text,
  seq: previous.seq + 1,
});

/**
 * The second sentence `NoRowsOverlay` paints when a filter or a search
 * emptied the grid. Lives here so the announcement and the pixels say the
 * same words — the overlay imports it, never the other way round.
 */
export const NO_RESULTS_GUIDANCE = 'Try adjusting filters or clear them all.';

/**
 * The settled criteria behind one announcement.
 *
 * `filterCount` is what the sentence COUNTS; `filterSignature` is what change
 * detection KEYS ON. They are separate because editing a filter's value
 * ("status is open" → "status is closed") leaves the count identical, and a
 * count-only comparison would go silent on the one change the user made.
 */
export type GridResultCriteria = {
  filterCount: number;
  filterSignature: string;
  searchText: string;
  quickFilterLabel: string;
};

/**
 * A filter narrows the result set only once it carries a `where`. `AddFilter`
 * puts an empty `{ field, where: [] }` chip on screen the moment a column is
 * ticked, before Apply — counting that as "applied" would announce a filter
 * that changed nothing.
 */
export const isAppliedFilter = (filter: ServerGridFilter): boolean => (filter.where?.length ?? 0) > 0;

export const gridResultCriteria = (
  filters: readonly ServerGridFilter[],
  searchText: string,
  quickFilterLabel: string
): GridResultCriteria => ({
  filterCount: filters.length,
  filterSignature: JSON.stringify(filters),
  searchText,
  quickFilterLabel,
});

export const gridCriteriaChanged = (previous: GridResultCriteria, next: GridResultCriteria): boolean =>
  previous.filterSignature !== next.filterSignature ||
  previous.searchText !== next.searchText ||
  previous.quickFilterLabel !== next.quickFilterLabel;

/** "no results" / "1 result" / "47 results" — never a bare number. */
export const gridResultCountPhrase = (resultCount: number): string => {
  if (resultCount <= 0) return 'no results';

  return resultCount === 1 ? '1 result' : `${resultCount} results`;
};

const appliedFiltersPhrase = (filterCount: number): string =>
  `${filterCount} filter${filterCount === 1 ? '' : 's'} applied`;

const capitalize = (sentence: string): string => sentence.charAt(0).toUpperCase() + sentence.slice(1);

/**
 * The outcome of ONE settled criteria change, or `''` when nothing changed.
 *
 * `resultCount` must be the count that BELONGS to `next` — the caller is
 * responsible for waiting until the rows caught up, which is why this
 * function takes a number rather than reaching for state itself.
 */
export const gridResultAnnouncement = (
  previous: GridResultCriteria,
  next: GridResultCriteria,
  resultCount: number
): string => {
  if (!gridCriteriaChanged(previous, next)) return '';

  const filtersChanged = previous.filterSignature !== next.filterSignature;
  const searchChanged = previous.searchText !== next.searchText;
  const quickFilterChanged = previous.quickFilterLabel !== next.quickFilterLabel;

  const clauses: string[] = [];

  if (filtersChanged && searchChanged && next.filterCount === 0 && !next.searchText) {
    // A reset clears both at once; "Filters cleared, Search cleared" says it twice.
    clauses.push('Filters and search cleared');
  } else {
    if (filtersChanged) {
      clauses.push(next.filterCount === 0 ? 'Filters cleared' : appliedFiltersPhrase(next.filterCount));
    }

    if (searchChanged) {
      clauses.push(next.searchText ? `Search "${next.searchText}"` : 'Search cleared');
    }
  }

  if (quickFilterChanged && next.quickFilterLabel) {
    clauses.push(`Showing ${next.quickFilterLabel}`);
  }

  const countPhrase = gridResultCountPhrase(resultCount);
  const outcome = clauses.length === 0 ? capitalize(countPhrase) : `${clauses.join(', ')}, ${countPhrase}`;

  // Guidance only where it is true: with no criteria left, there is nothing to adjust.
  const hasActiveCriteria = next.filterCount > 0 || next.searchText.length > 0;

  return resultCount <= 0 && hasActiveCriteria ? `${outcome}. ${NO_RESULTS_GUIDANCE}` : outcome;
};

/** Nouns for the selection sentence — "4 invoices selected", not "4 rows selected". */
export type GridSelectionNouns = { singular: string; plural: string };

export const gridSelectionNouns = (entityName?: string, entityNamePlural?: string): GridSelectionNouns => ({
  singular: entityName ?? 'row',
  plural: entityNamePlural ?? (entityName ? `${entityName}s` : 'rows'),
});

/**
 * "4 invoices selected" — the sentence `BulkActionToolbar` shows but never
 * announced, because the bar MOUNTS with its text already in place and a
 * live region has to exist before its content changes to be spoken.
 */
export const gridSelectionAnnouncement = (
  previousCount: number,
  selectedCount: number,
  nouns: GridSelectionNouns
): string => {
  if (selectedCount === previousCount) return '';
  if (selectedCount <= 0) return 'Selection cleared';

  return `${selectedCount} ${selectedCount === 1 ? nouns.singular : nouns.plural} selected`;
};
